import { Blog } from 'almanac-ui/icons/blaze/24/Blog'
import { object, string } from 'yup'
import { FormSchema } from '../Wizard/Form/Types'
import { audience } from './shared/audience'
import { includeImages } from './shared/includeImages'
import { brandVoiceId, tone } from './shared/voiceTones'
import { safify } from './utils/safify'
import { sourceMaterialIds } from './shared/sourceMaterials'
import { language } from './shared/language'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { topic } from 'components/Wizards/schemas/shared/topic'
import { seoWords } from 'components/Wizards/schemas/shared/seoWords'
import { BlogPostLength } from 'components/Wizards/BlogPostWizard/Types'
import { callToAction } from 'components/Wizards/schemas/shared/callToAction'
import { avoid } from 'components/Wizards/schemas/shared/avoid'
import { additionalInfo } from 'components/Wizards/schemas/shared/additionalInfo'
import { productDescription } from 'components/Wizards/schemas/shared/productDescription'
import { MultiStepSchema, StepSchema } from 'components/Wizards/schemas/Types'
import { SelectImages } from 'components/Wizards/schemas/shared/selectImages'
import { brandKitId } from './shared/brandKitId'
import { TextChannels } from '../../../constants'
import { validLinks } from './shared/validLinks'
import { blogExternalIntegrationId } from './shared/externalIntegrationId'

const meta = {
  type: ChannelType.BlogPost,
  icon: Blog,
  title: TextChannels[ChannelType.BlogPost].name,
  previewUrl:
    'https://res.cloudinary.com/almanac/image/upload/v1710183787/Home%20Card%20Previews/xfpedur3adlb5odloqfm.png',
}

const fields = {
  topic: topic.meta({ label: 'What would you like to write about?', generationType: meta.type }),
  goal: string()
    .oneOf([
      'promotional',
      'informational',
      'narrative',
      'how to',
      'ranked list',
      'comparison',
      'custom',
      'comparison',
      'custom',
    ])
    .meta({ label: "What's the goal of this blog post?", autoFill: true })
    .default('promotional')
    .transform(safify),
  audience: audience.meta({ label: 'Who is the target audience?', autoFill: true }),
  blog_length: string()
    .oneOf(Object.values(BlogPostLength))
    .default(BlogPostLength.Medium)
    .meta({ label: 'How long should the post be?', autoFill: true })
    .transform(safify),
  seo_words: seoWords.meta({ label: 'What keywords should be included?' }),
  language,
  brand_voice_id: brandVoiceId,
  brand_kit_id: brandKitId,
  external_integration_id: blogExternalIntegrationId,
  include_images: includeImages.meta({
    description: 'An image will make your posts far more engaging and attention grabbing',
  }),
  source_material_ids: sourceMaterialIds,
  tone,
  call_to_action: callToAction.meta({ collapsible: true }),
  author_persona: string()
    .default('')
    .meta({
      label: 'Author Persona',
      placeholder: 'Who should the post be written as (e.g. Tech CEO)',
      mobilePlaceholder: 'Who should the post be written as',
      snippets: true,
      autoFill: true,
      collapsible: true,
    })
    .transform(safify),
  product_description: productDescription.meta({ collapsible: true }),
  avoid: avoid.meta({ collapsible: true }),
  additional_info: additionalInfo.meta({ collapsible: true }),
}

export const BlogPostSchema = object(fields).meta(meta) as FormSchema<ChannelType.BlogPost>

const imagesStep = SelectImages.meta({ submitLabel: 'Generate Post', showIncludeImages: true }) as StepSchema

const selectTitleStep = object({
  selected_title_id: string().default('').required().transform(safify),
  seo_words: seoWords.meta({ label: 'What keywords should be included?' }),
}).meta({
  title: 'Title',
  submitLabel: 'Generate Outline',
}) as StepSchema

const selectOutlineStep = object({
  title: string().default('').required().transform(safify),
  introduction: string().default('').required().transform(safify),
  outline: string().default('').required().transform(safify),
}).meta({
  title: 'Outline',
  submitLabel: 'Generate Links',
  type: ChannelType.BlogPost,
}) as StepSchema

const selectLinksStep = object({
  external_links: validLinks,
}).meta({
  title: 'Links',
  submitLabel: 'Generate Post',
  type: ChannelType.BlogPost,
}) as StepSchema

export const BlogPostMultiStepSchema: MultiStepSchema<ChannelType.BlogPost> = {
  steps: [
    BlogPostSchema.omit(['include_images', 'external_integration_id']).meta({
      submitLabel: 'Generate Titles',
    }) as StepSchema,
    selectTitleStep,
    selectOutlineStep,
    selectLinksStep,
    imagesStep,
  ],
  meta: {
    ...meta,
    type: ChannelType.BlogPost,
    channel: ChannelType.BlogPost,
  },
}
